const AMM_TRADE_KIND = '1';
const UNSIGNED_INTEGER = /^(?:0|[1-9]\d*)$/;

interface WalletTotals {
  readonly publicKey: string;
  readonly totalVolume: string;
  readonly totalTrades: number;
}

interface UserStatsLike {
  readonly totalVolume?: string;
  readonly totalTrades?: number | null;
  readonly wallets?: readonly WalletTotals[];
}

/**
 * Trade events carry the actor wallet's post-trade lifetime totals. Max-merge
 * them into that wallet's entry: monotonic totals make it idempotent and
 * order-independent, so duplicates, replays, and cross-market reordering from
 * parallel indexer shards all converge. Top-level totals are the wallet sums,
 * matching `GET /v1/users/{identifier}`. AMM activity shares one subject with
 * liquidity/configuration kinds, so only TRADE counts. Returns `prev` untouched
 * when nothing changes (stale event, missing fields, unknown actor).
 */
export function applyWalletLifetimeTotals<T extends UserStatsLike>(
  prev: T,
  raw: Record<string, unknown>,
): T {
  if (raw.kind !== undefined && unsignedInteger(raw.kind) !== AMM_TRADE_KIND) return prev;
  const volume = unsignedInteger(raw.actorLifetimeVolumeQuoteD6);
  const trades = unsignedInteger(raw.actorLifetimeTrades);
  if (volume === null || trades === null || prev.wallets === undefined) return prev;

  const index = prev.wallets.findIndex((wallet) => wallet.publicKey === raw.actor);
  const entry = prev.wallets[index];
  if (entry === undefined) return prev;

  const nextVolume = maxBigint(entry.totalVolume, volume);
  const nextTrades = maxBigint(String(entry.totalTrades), trades);
  if (nextVolume === entry.totalVolume && nextTrades === String(entry.totalTrades)) return prev;

  const wallets = prev.wallets.map((wallet, i) =>
    i === index ? { ...wallet, totalVolume: nextVolume, totalTrades: Number(nextTrades) } : wallet,
  );
  return {
    ...prev,
    wallets,
    totalVolume: wallets.reduce((sum, wallet) => sum + BigInt(wallet.totalVolume), 0n).toString(),
    totalTrades: wallets.reduce((sum, wallet) => sum + wallet.totalTrades, 0),
  };
}

function maxBigint(current: string, incoming: string): string {
  return BigInt(incoming) > BigInt(current) ? incoming : current;
}

function unsignedInteger(value: unknown): string | null {
  if (typeof value === 'bigint') return value >= 0n ? value.toString() : null;
  if (typeof value === 'number') {
    return Number.isSafeInteger(value) && value >= 0 ? String(value) : null;
  }
  return typeof value === 'string' && UNSIGNED_INTEGER.test(value) ? value : null;
}
